/**
 * Aide contextuelle — Page unifiée /admin/e2e-monitor (smoke + crawler).
 *
 * Fusion 2026-05-24 : avant cette date, le crawler avait sa propre page
 * /admin/e2e-crawler (help/crawler.js). Les deux pages ont été fusionnées
 * en une seule à 3 onglets (Vue d'ensemble, Smoke, Crawler).
 */

const e2eMonitorHelp = {
  '/admin/e2e-monitor': {
    title: 'Monitoring E2E (smoke + crawler exhaustif)',
    user: [
      {
        heading: 'À quoi sert ce dashboard ?',
        content:
          'Page unifiée pour surveiller les deux suites E2E du portail :\n\n' +
          '**Smoke (canari court)** — quelques tests Playwright critiques exécutés 2×/jour (03:30 + 13:00 UTC) pour détecter rapidement une régression bloquante.\n\n' +
          '**Crawler (audit exhaustif)** — robot Playwright qui parcourt **chaque nuit à 02:00 UTC** l\'intégralité du portail pour 6 profils utilisateurs différents (superadmin, admin_n1 interne, tech_n2 interne, tech_n1 interne, tech_n1 externe, client). Sur chaque page visitée et chaque endpoint appelé, 5 détecteurs cherchent des anomalies et créent automatiquement un signalement dans **/admin/bug-reports** avec dédup par signature.',
      },
      {
        heading: 'Onglet "Vue d\'ensemble"',
        content:
          'Encart **Planification** : statut/prochaine occurrence des deux crons.\n\n' +
          'Encart **Rapports HTML** : liens vers e2e.meteris.fr (smoke) et e2e.meteris.fr/crawler/ (crawler).\n\n' +
          'Encart **KPIs simples** : taux de réussite des 30 derniers runs smoke + statut dernier run + nombre de runs crawler enregistrés.',
      },
      {
        heading: 'Onglet "Smoke (canari court)"',
        content:
          'Bandeau dernier run (passé/échoué, timestamps, déclencheur).\n\n' +
          'Bouton **Relancer maintenant** pour déclencher un run manuel hors-cron.\n\n' +
          'Tableau historique des 50 derniers runs (cron + manuel) avec colonne **Action** : lien rapport HTML + bouton **Screenshots** (sur run en échec) pour voir les copies d\'écran capturées par Playwright.',
      },
      {
        heading: 'Onglet "Crawler (audit exhaustif)"',
        content:
          'Statut runner + boutons **Smoke run** (~2 min, superadmin uniquement) et **Full run** (6 profils, ~10-15 min).\n\n' +
          'Sous-onglets :\n' +
          '**Runs** — historique des 30 derniers runs avec durée, pages visitées, endpoints testés, bugs new/récurrents.\n' +
          '**Bugs ouverts** — iframe filtrée sur source=e2e-crawler + status=new.\n' +
          '**Matrice** — fichier JSON listant pour chaque endpoint la permission requise et le comportement attendu par profil.\n' +
          '**Skip-list** — sélecteurs DOM et patterns d\'erreur ignorés (faux-positifs connus).',
      },
      {
        heading: 'Détecteurs crawler actifs',
        content:
          '**api-permissions** — Comparaison réponse réelle vs matrice attendue (401/403 attendu mais 200 reçu = leak).\n' +
          '**ui-console** — Erreurs ou warnings JavaScript détectés dans la console pendant la visite.\n' +
          '**ui-network** — Requêtes XHR/fetch qui retournent 4xx/5xx.\n' +
          '**ui-render** — Rendu visiblement cassé (page blanche, layout effondré).\n' +
          '**ui-deadbutton** — Boutons ou liens cliqués sans aucun effet observable (ni navigation, ni requête, ni toast).',
      },
    ],
    admin: [
      {
        heading: 'Architecture déploiement smoke',
        content:
          'Container `meteris-e2e-runner` sur docker1 expose une API HTTP interne sur :8000. Le cron `/opt/e2e-monitor/run.sh` clone le repo, lance `npx playwright test`, copie le rapport vers `/opt/e2e-monitor/report/` (servi par nginx sur https://e2e.meteris.fr) puis persiste le run dans `col_e2e_runs` via `POST /api/admin/e2e/internal/persist-run` (auth `X-E2E-Runner-Secret`).',
      },
      {
        heading: 'Architecture déploiement crawler',
        content:
          'Container `meteris-e2e-crawler` sur docker1 (stack séparée). Backend FastAPI expose 3 endpoints admin (`run` / `runs` / `status`, permission **`admin.crawler`**) et 5 endpoints internes (header `X-E2E-Crawler-Secret`). Les 6 users de test (`e2e-crawler-*@meteris.fr`) sont seedés via `scripts/seed_e2e_crawler_users.py` avec flag **`is_e2e_test=True`** (exclus des KPIs et des listes par défaut).',
      },
      {
        heading: 'Endpoints backend',
        content:
          '**Smoke**\n' +
          '`GET /api/admin/e2e/status`, `POST /api/admin/e2e/run`, `GET /api/admin/e2e/runs?limit=50`\n' +
          '`GET /api/admin/e2e/screenshots/{run_id}` — liste les screenshots disponibles\n' +
          '`GET /api/admin/e2e/screenshots/{run_id}/{name}` — stream un screenshot PNG\n\n' +
          '**Crawler**\n' +
          '`GET /api/admin/crawler/status`, `POST /api/admin/crawler/run`, `GET /api/admin/crawler/runs?limit=30`',
      },
      {
        heading: 'Screenshots smoke',
        content:
          'Playwright capture les screenshots `screenshot: only-on-failure` dans `test-results/*/test-failed-N.png`. À chaque run, `/opt/e2e-monitor/run.sh` extrait ces PNG et les copie vers `/opt/e2e-monitor/report/screenshots/<run_id>/` (servi par nginx sur https://e2e.meteris.fr/screenshots/<run_id>/<name>.png).\n\n' +
          'Le bouton **Screenshots** de l\'onglet Smoke ouvre une lightbox listant ces PNG via `/api/admin/e2e/screenshots/{run_id}` (qui scanne le filesystem nginx via SSH).\n\n' +
          'Note : si un test échoue au niveau API (avant navigation), aucun screenshot n\'est généré — le rapport HTML reste la référence dans ce cas.',
      },
      {
        heading: 'Notifications & alertes crawler',
        content:
          '**Mail immédiat** au boss si un bug `critical` est détecté (1ère occurrence uniquement).\n' +
          '**Digest hebdo** dimanche ~19h CEST avec stats 7j (nouveaux/résolus/ouverts) via tâche planifiée `crawler_weekly_digest`.\n' +
          '**Notif portail** (cloche) sur tout nouveau bug crawler.\n' +
          '**Auto-close** des bugs e2e-crawler après 7 jours sans nouvelle occurrence (tâche `crawler_auto_close_stale_bugs`).',
      },
      {
        heading: 'Mise à jour matrice / skip-list crawler',
        content:
          'Quand un faux-positif récurrent apparaît :\n' +
          '1. Identifier le bug récurrent dans `/admin/bug-reports?source=e2e-crawler`.\n' +
          '2. Ajouter le sélecteur (skip-selectors.json) ou le pattern (ignored-console-patterns.json) dans `apps/e2e-crawler/config/`.\n' +
          '3. Commit + redéploy le runner.\n' +
          '4. Marquer les bugs concernés en `wontfix` ou `closed` selon le cas.',
      },
    ],
  },
};

export default e2eMonitorHelp;
